import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  IconButton,
  CircularProgress,
  Backdrop,
  Stack,
  Tooltip,
  Typography,
  List,
  Collapse,
  Paper,
  Box,
  Button,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import { Delete } from "@mui/icons-material";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import ChecklistCard from "./ChecklistCard";
import SortDialog from "../Dialogs/SortDialog";
import ChecklistDialog from "./ChecklistDialog";
import { getSortArray } from "../helpers/getSortArray";
import { firestoreAutoId } from "../helpers/firestoreAutoId";
import { saveToFirestore } from "../helpers/saveToFirestore";
import { castDateToFireStoreDate } from "../helpers/castDateToFireStoreDate";
import { getUserAccess } from "../helpers/getUserAccess";
import { useNavigate } from "react-router-dom";

export default function ChecklistsList(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [checklists, setChecklists] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplates, setSelectedTemplates] = React.useState([]);
  const [allChecklists, setAllChecklists] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);
  const [selectedEquipment, setSelectedEquipment] = React.useState([]);
  const [filterValue, setFilterValue] = React.useState("");
  const [sortByValue, setSortByValue] = React.useState("Asset Name");
  const [sortByOptions] = React.useState([
    "Asset Name",
    "Asset Name Desc",
    "Name",
    "Name Desc",
    "Created Date",
    "Created Date Desc",
  ]);
  const [project, setProject] = React.useState({});
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);

  const [activeStep, setActiveStep] = React.useState(0);
  const [importFields, setImportFields] = React.useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        var proj = props.project;

        setProject(proj);
        setEquipment(proj.Equipment);
        const cklsts =
          props.equipmentId !== null
            ? proj.Checklists.filter(
                (c) => c.equipment_id === props.equipmentId,
              )
            : proj.Checklists;
        setAllChecklists([...cklsts]);
        onSortChange(sortByValue, cklsts);
        setTemplates(proj.ChecklistTemplates || []);
      } catch (error) {
        console.error("Error getting data:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const formatChecklists = (templates) => {
    const updatedChecklists = templates.map((tpl) => {
      tpl.template_id = tpl.id;
      return {
        ...tpl,
        equipment: selectedEquipment.map((eq) => ({
          id: eq.id,
          name: eq.Name,
        })),
      };
    });
    var cklsts = [];
    updatedChecklists.forEach((chk) => {
      chk.equipment.forEach((eq) => {
        var cklst = { ...chk };
        delete cklst.equipment;
        cklst["equipment_id"] = eq.id;
        cklst["Asset Name"] = eq.name;
        cklst["Selected"] = false;
        cklst["id"] = "new";
        cklst = applyEditTrackingFields(cklst, props.user);
        cklst["id"] = firestoreAutoId();
        if (cklst["Checklist Type"] === "Pre-Functional & Receipt Inspection") {
          cklst["Checklist Type"] = "Pre-Functional";
          cklsts.push({ ...cklst });
          cklst["id"] = firestoreAutoId();
          cklst["Checklist Type"] = "Receipt Inspection";
          cklsts.push(cklst);
        } else {
          cklsts.push(cklst);
        }
      });
    });
    return cklsts.sort((a, b) => {
      const cityComparison = a["Asset Name"].localeCompare(b["Asset Name"]);
      if (cityComparison !== 0) {
        return cityComparison;
      }
      return a["Name"].localeCompare(b["Name"]);
    });
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
  };

  const saveSelectedEquip = async (selectedEquip) => {
    setSelectedEquipment(selectedEquip);
  };

  const saveSelectedTemplates = async (selectedTemplates) => {
    selectedTemplates.forEach((tpl) => {
      if (tpl.hasOwnProperty("Checklist Type") === false) {
        tpl["Checklist Type"] = "";
      }
      if (tpl.hasOwnProperty("Assigned to") === false) {
        tpl["Assigned to"] = "";
      }
    });
    setSelectedTemplates(selectedTemplates);
  };

  const onChecklistTypeChange = (e) => {
    if (e.target.name === "all") {
      selectedTemplates.forEach((tpl) => {
        tpl["Checklist Type"] = e.target.value;
      });
    } else {
      var tpl = selectedTemplates.find((t) => t.id === e.target.name);
      tpl["Checklist Type"] = e.target.value;
    }
    setSelectedTemplates([...selectedTemplates]);
  };

  const onChecklistNameChange = (e) => {
    var tpl = selectedTemplates.find((t) => t.id === e.target.name);
    tpl["Name"] = e.target.value;
    setSelectedTemplates([...selectedTemplates]);
  };

  const onChecklistAssignToChange = (e) => {
    if (e.target.name === "all") {
      selectedTemplates.forEach((tpl) => {
        tpl["Assigned to"] = e.target.value;
      });
    } else {
      var tpl = selectedTemplates.find((t) => t.id === e.target.name);
      tpl["Assigned to"] = e.target.value;
    }
    setSelectedTemplates([...selectedTemplates]);
  };

  const onNextClick = async () => {
    if (activeStep === 2) {
      //Final step - save the checklists
      try {
        var cklsts = formatChecklists(selectedTemplates);
        project.Checklists = [...project.Checklists, ...cklsts];
        const updatedProject = { ...project };
        await saveToFirestore("projects", updatedProject);
        setProject({ ...updatedProject });

        var cleanArray = [];
        cklsts.forEach((cklst) => {
          cleanArray.push(castDateToFireStoreDate(cklst));
        });
        setAllChecklists([...allChecklists, ...cleanArray]);
        onSortChange(sortByValue, [...checklists, ...cleanArray]);
        enqueueSnackbar("Checklists saved successfully", {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "success",
        });
        setActiveStep(0);
        setImportDialogOpen(false);
      } catch (error) {
        console.error("Error saving checklists:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      }
    } else {
      //Proceed to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const onBackClick = () => {
    //Proceed to next step
    if (activeStep === 0) {
      onCloseImportDialog();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const onImportExcel = async () => {};

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allChecklists.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });
      setChecklists([...filteredData]);
    } else {
      setChecklists([...allChecklists]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    if (value === "Asset Name") {
      //This groups by Asset Name then Name
      setChecklists([
        ...obs.sort((a, b) => {
          const cityComparison = a["Asset Name"].localeCompare(b["Asset Name"]);
          if (cityComparison !== 0) {
            return cityComparison;
          }
          return a["Name"].localeCompare(b["Name"]);
        }),
      ]);
    } else {
      setChecklists([...obs]);
    }
  };

  const onAddChecklist = () => {
    try {
      var item = {};

      item["id"] = "new";

      item = applyEditTrackingFields(item, props.user);

      setImportDialogOpen(true);
      setSelectedEquipment(
        props.equipmentId !== null
          ? project.Equipment.filter((e) => e.id === props.equipmentId)
          : [],
      );
      setSelectedTemplates([]);
      setActiveStep(0);
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const handleChecked = (e) => {
    const item = checklists.find((item) => item.id === e.target.id);
    item.Selected = !item.Selected;
    setChecklists([...checklists]);
  };

  const onDeleteChecklists = async () => {
    try {
      setIsLoading(true);
      const remainingChecklists = checklists.filter(
        (item) => item.Selected === false,
      );
      const savedProj = { ...project, Checklists: remainingChecklists };

      await saveToFirestore("projects", savedProj);
      setProject({ ...savedProj });
      setChecklists([...remainingChecklists]);
      onSortChange(sortByValue, remainingChecklists);
      enqueueSnackbar("Selected checklists deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onUncheckSelected = () => {
    checklists.forEach((item) => {
      if (item.Selected === true) {
        item.Selected = false;
      }
    });
    setChecklists([...checklists]);
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div>
      <Stack
        // style={{ paddingTop: 10 }}
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        {project.ChecklistTemplates.length > 0 && (
          <Tooltip title="Add Checklist">
            <IconButton
              onClick={onAddChecklist}
              aria-label="Add Checklist"
              disabled={
                !getUserAccess(
                  props.user,
                  props.project.uid,
                  props.project.Contacts,
                ).allowCreate
              }
              size="large"
              color="primary"
            >
              <AddBoxIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack
        style={{ paddingTop: 30 }}
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {allChecklists.length > 0 && (
          <SortDialog
            sortByValue={sortByValue}
            onSortChange={onSortChange}
            objArray={checklists}
            sortByOptions={sortByOptions}
            filterValue={filterValue}
            onFilterChange={onFilterChange}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}

        <Collapse
          style={{ width: "100%" }}
          in={checklists.some((item) => item.Selected === true)}
        >
          <Paper elevation={6} style={{ width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                height: 60,
                borderRadius: 1,
                bgcolor: "secondary.light",
                display: { xs: "none", md: "block" },
                // "&:hover": {
                //   bgcolor: "primary.dark",
                // },
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
                style={{ height: "100%", paddingLeft: 16 }}
              >
                <Typography color="primary.contrastText">
                  {`Selected ${
                    checklists.filter((f) => f.Selected === true).length
                  }`}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUncheckSelected}
                  startIcon={<UnpublishedIcon />}
                >
                  Uncheck All
                </Button>

                {/* <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<EditDocument />}
                >
                  {`Perform Checklist${
                    checklists.filter((f) => f.Selected === true).length > 1
                      ? "s"
                      : ""
                  }`}
                </Button> */}

                {/* <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Delete />}
                  onClick={onDeleteChecklists}
                >
                  {`Delete Selected`}
                </Button> */}
                {/*      <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Photo />}
                  >
                    Manage Photos
                  </Button> */}
              </Stack>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: 60,
                borderRadius: 1,
                bgcolor: "primary.main",
                display: { xs: "block", md: "none" },
                // "&:hover": {
                //   bgcolor: "primary.dark",
                // },
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
                style={{ height: "100%", paddingLeft: 16 }}
              >
                <Typography color="primary.contrastText">
                  {`Selected ${
                    checklists.filter((f) => f.Selected === true).length
                  }`}
                </Typography>

                <Tooltip title={`Uncheck All`}>
                  <IconButton color="secondary" onClick={onUncheckSelected}>
                    <UnpublishedIcon />
                  </IconButton>
                </Tooltip>
                {/* <Tooltip
                  title={`Perform Checklist${
                    checklists.filter((f) => f.Selected === true).length > 1
                      ? "s"
                      : ""
                  }`}
                >
                  <IconButton color="secondary" onClick={null}>
                    <EditDocument />
                  </IconButton>
                </Tooltip> */}

                <Tooltip title={`Delete Selected`}>
                  <IconButton color="secondary" onClick={onDeleteChecklists}>
                    <Delete />
                  </IconButton>
                </Tooltip>
                {/*      <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Photo />}
                  >
                    Manage Photos
                  </Button> */}
              </Stack>
            </Box>
          </Paper>
        </Collapse>

        {project.ChecklistTemplates.length === 0 ? (
          <Typography>
            There aren't any checklist templates associated with this project.
            Please add{" "}
            <Tooltip title="Add new checklist template">
              <IconButton
                onClick={() =>
                  navigate(`/project/${project.id}/checklist-templates`)
                }
                aria-label="Add new checklist template"
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                size="medium"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            a new checklist template before creating checklists.
          </Typography>
        ) : checklists.length === 0 && filterValue.length <= 2 ? (
          <Typography>
            There aren't any checklists associated with this{" "}
            {props.equipmentId !== null ? "equipment" : "project"}. Please add
            <Tooltip title="Add new checklist">
              <IconButton
                onClick={onAddChecklist}
                aria-label="Add new checklist"
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                size="medium"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            a new checklist.
          </Typography>
        ) : checklists.length === 0 && filterValue.length > 2 ? (
          <Typography>
            There aren't any checklists associated with your search: "
            <strong>{filterValue}</strong>".
          </Typography>
        ) : (
          <List style={{ width: "100%" }}>
            <ChecklistCard
              checklists={checklists}
              page={page}
              rowsPerPage={rowsPerPage}
              allowUpdate={
                getUserAccess(
                  props.user,
                  props.project.uid,
                  props.project.Contacts,
                ).allowUpdate
              }
              // onEdit={onEdit}
              projectId={project.id}
              handleChecked={handleChecked}
            />
          </List>
        )}
      </Stack>
      <ChecklistDialog
        isOpen={importDialogOpen}
        onClose={onCloseImportDialog}
        onFldChange={onFldChange}
        onAttributeChange={onAttributeChange}
        onImportExcel={onImportExcel}
        selectedEquipment={selectedEquipment}
        equip={equipment}
        templates={templates}
        saveSelectedEquip={saveSelectedEquip}
        saveSelectedTemplates={saveSelectedTemplates}
        onNextClick={onNextClick}
        onBackClick={onBackClick}
        activeStep={activeStep}
        selectedTemplates={selectedTemplates}
        onChecklistTypeChange={onChecklistTypeChange}
        onChecklistNameChange={onChecklistNameChange}
        onChecklistAssignToChange={onChecklistAssignToChange}
      />
    </div>
  );
}
